/**
 * ============================================================
 * EXTRACT ARRAY FROM API RESPONSE
 * ============================================================
 */
export const extractArray = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
};

/**
 * ============================================================
 * MEMBER COUNT
 * ============================================================
 */
export const getWorkspaceMemberCount = (workspace) => {
  if (!workspace) {
    return 0;
  }

  if (
    workspace.member_count !== undefined &&
    workspace.member_count !== null
  ) {
    return Number(workspace.member_count);
  }

  if (
    workspace.members_count !== undefined &&
    workspace.members_count !== null
  ) {
    return Number(workspace.members_count);
  }

  if (Array.isArray(workspace.members)) {
    return workspace.members.length;
  }

  return 0;
};

/**
 * ============================================================
 * ITEM COUNT
 * ============================================================
 */
export const getWorkspaceItemCount = (workspace) => {
  if (!workspace) {
    return 0;
  }

  if (
    workspace.item_count !== undefined &&
    workspace.item_count !== null
  ) {
    return Number(workspace.item_count);
  }

  if (
    workspace.items_count !== undefined &&
    workspace.items_count !== null
  ) {
    return Number(workspace.items_count);
  }

  if (
    workspace.total_tasks !== undefined &&
    workspace.total_tasks !== null
  ) {
    return Number(workspace.total_tasks);
  }

  if (Array.isArray(workspace.items)) {
    return workspace.items.length;
  }

  if (Array.isArray(workspace.tasks)) {
    return workspace.tasks.length;
  }

  return 0;
};

/**
 * ============================================================
 * COMPLETED ITEM COUNT
 * ============================================================
 */
export const getCompletedItemCount = (workspace) => {
  if (!workspace) {
    return 0;
  }

  if (
    workspace.completed_items !== undefined &&
    workspace.completed_items !== null
  ) {
    return Number(workspace.completed_items);
  }

  if (
    workspace.completed_tasks !== undefined &&
    workspace.completed_tasks !== null
  ) {
    return Number(workspace.completed_tasks);
  }

  const items = Array.isArray(workspace.items)
    ? workspace.items
    : Array.isArray(workspace.tasks)
    ? workspace.tasks
    : [];

  return items.filter((item) => {
    const status = String(
      item?.status || ""
    ).trim().toLowerCase();

    return (
      status === "completed" ||
      status === "complete" ||
      status === "done"
    );
  }).length;
};

/**
 * ============================================================
 * WORKSPACE PROGRESS
 *
 * completed tasks / total tasks * 100
 * ============================================================
 */
export const getWorkspaceProgress = (workspace) => {
  if (!workspace) {
    return 0;
  }

  const totalItems =
    getWorkspaceItemCount(workspace);

  const completedItems =
    getCompletedItemCount(workspace);

  if (totalItems === 0) {
    return 0;
  }

  return Math.min(
    Math.max(
      Math.round(
        (completedItems / totalItems) * 100
      ),
      0
    ),
    100
  );
};

/**
 * ============================================================
 * WORKSPACE SUMMARY
 * ============================================================
 */
export const getWorkspaceSummary = (workspace) => {
  return {
    members:
      getWorkspaceMemberCount(workspace),

    items:
      getWorkspaceItemCount(workspace),

    completedItems:
      getCompletedItemCount(workspace),

    progress:
      getWorkspaceProgress(workspace),
  };
};

/**
 * ============================================================
 * PROJECT MEMBER COUNT
 * ============================================================
 */
export const getProjectMemberCount = (
  project,
  workspaces = []
) => {
  if (!project) {
    return 0;
  }

  if (
    project.member_count !== undefined &&
    project.member_count !== null
  ) {
    return Number(project.member_count);
  }

  if (Array.isArray(project.members)) {
    return project.members.length;
  }

  const memberIds = new Set();

  workspaces.forEach((workspace) => {
    const members = Array.isArray(
      workspace.members
    )
      ? workspace.members
      : [];

    members.forEach((member) => {
      const id =
        member?.uuid ||
        member?.employee_uuid ||
        member?.employeeUuid ||
        member?.id;

      if (id) {
        memberIds.add(id);
      }
    });
  });

  return memberIds.size;
};

/**
 * ============================================================
 * PROJECT WORKSPACE COUNT
 * ============================================================
 */
export const getProjectWorkspaceCount = (
  project,
  workspaces = []
) => {
  if (Array.isArray(workspaces)) {
    return workspaces.length;
  }

  return Number(
    project?.workspace_count ||
      project?.workspaces_count ||
      0
  );
};

/**
 * ============================================================
 * PROJECT TOTAL ITEMS
 * ============================================================
 */
export const getProjectItemCount = (
  workspaces = []
) => {
  if (!Array.isArray(workspaces)) {
    return 0;
  }

  return workspaces.reduce(
    (total, workspace) => {
      return (
        total +
        getWorkspaceItemCount(workspace)
      );
    },
    0
  );
};

/**
 * ============================================================
 * PROJECT COMPLETED ITEMS
 * ============================================================
 */
export const getProjectCompletedItemCount = (
  workspaces = []
) => {
  if (!Array.isArray(workspaces)) {
    return 0;
  }

  return workspaces.reduce(
    (total, workspace) => {
      return (
        total +
        getCompletedItemCount(workspace)
      );
    },
    0
  );
};

/**
 * ============================================================
 * PROJECT PROGRESS
 * ============================================================
 */
export const getProjectProgress = (
  workspaces = []
) => {
  const totalItems =
    getProjectItemCount(workspaces);

  const completedItems =
    getProjectCompletedItemCount(
      workspaces
    );

  if (totalItems === 0) {
    return 0;
  }

  return Math.round(
    (completedItems / totalItems) * 100
  );
};

/**
 * ============================================================
 * PROJECT SUMMARY
 * ============================================================
 */
export const getProjectSummary = (
  project,
  workspaces = []
) => {
  const totalItems =
    getProjectItemCount(workspaces);

  const completedItems =
    getProjectCompletedItemCount(
      workspaces
    );

  return {
    members:
      getProjectMemberCount(
        project,
        workspaces
      ),

    workspaces:
      getProjectWorkspaceCount(
        project,
        workspaces
      ),

    items:
      totalItems,

    completedItems:
      completedItems,

    progress:
      getProjectProgress(
        workspaces
      ),
  };
};